import React, { useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import axios from "axios";
import styled from "styled-components";

// Replace with your Stripe publishable key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const CardElementContainer = styled.div`
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: white;
  margin-bottom: 16px;
`;

const Button = styled.button`
  background: ${(props) => (props.disabled ? "#cccccc" : "#5469d4")};
  color: white;
  border: 0;
  padding: 12px 16px;
  border-radius: 4px;
  font-weight: 600;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s ease;
  display: block;
  width: 100%;

  &:hover {
    background: ${(props) => (props.disabled ? "#cccccc" : "#4050b5")};
  }
`;

const Message = styled.div`
  margin-top: 16px;
  padding: 12px;
  border-radius: 4px;
  background: ${(props) => (props.type === "error" ? "#ffebee" : "#e8f5e9")};
  color: ${(props) => (props.type === "error" ? "#c62828" : "#2e7d32")};
`;

const Form = styled.form`
  max-width: 400px;
  margin: 0 auto;
  padding: 20px;
`;

const CheckoutForm = ({ amount, onSuccess, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js hasn't loaded yet
      return;
    }

    setLoading(true);
    setMessage({ text: "", type: "" });

    try {
      // Call your backend to create the PaymentIntent
      const { data } = await axios.post("/api/payments/create-payment-intent", {
        amount: Math.round(amount * 100), // Convert to cents
        currency: "usd",
      });

      const { error: stripeError, paymentIntent } =
        await stripe.confirmCardPayment(data.clientSecret, {
          payment_method: {
            card: elements.getElement(CardElement),
            billing_details: {
              // You can add billing details here if needed
            },
          },
        });

      if (stripeError) {
        setMessage({
          text: stripeError.message || "An error occurred with your payment.",
          type: "error",
        });
        onError?.(stripeError);
      } else if (paymentIntent.status === "succeeded") {
        setMessage({
          text: "Payment successful!",
          type: "success",
        });
        onSuccess?.(paymentIntent);
      }
    } catch (err) {
      setMessage({
        text: err.response?.data?.detail || "An unexpected error occurred.",
        type: "error",
      });
      onError?.(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      <CardElementContainer>
        <CardElement
          options={{
            style: {
              base: {
                fontSize: "16px",
                color: "#424770",
                "::placeholder": {
                  color: "#aab7c4",
                },
              },
              invalid: {
                color: "#9e2146",
              },
            },
          }}
        />
      </CardElementContainer>
      <Button type="submit" disabled={!stripe || loading}>
        {loading ? "Processing..." : `Pay $${amount.toFixed(2)}`}
      </Button>
      {message.text && <Message type={message.type}>{message.text}</Message>}
    </Form>
  );
};

const StripeCheckout = ({ amount, onSuccess, onError }) => {
  if (!amount || amount <= 0) {
    return <Message type="error">Invalid amount</Message>;
  }

  return (
    <Elements stripe={stripePromise}>
      <CheckoutForm amount={amount} onSuccess={onSuccess} onError={onError} />
    </Elements>
  );
};

export default StripeCheckout;
